/**
 * TEMPORARY visual-comparison page for picking blog visuals.
 * Renders the 4 candidates (current / _2 light / _3 dark / _4 content)
 * for every interactive chart, and 3 candidates for covers and figures,
 * side by side.
 * Route: /blog-compare (registered in CRM.tsx). Not for production use.
 */
import type { ComponentType } from "react";

import { RaisyStackChart } from "../sections/visualizations/RaisyStackChart";
import { RaisyStackChart_2 } from "../sections/visualizations/RaisyStackChart_2";
import { RaisyStackChart_3 } from "../sections/visualizations/RaisyStackChart_3";
import { RaisyStackChart_4 } from "../sections/visualizations/RaisyStackChart_4";
import { CostGapChart } from "../sections/visualizations/CostGapChart";
import { CostGapChart_2 } from "../sections/visualizations/CostGapChart_2";
import { CostGapChart_3 } from "../sections/visualizations/CostGapChart_3";
import { CostGapChart_4 } from "../sections/visualizations/CostGapChart_4";
import { BotCareerPathChart } from "../sections/visualizations/BotCareerPathChart";
import { BotCareerPathChart_2 } from "../sections/visualizations/BotCareerPathChart_2";
import { BotCareerPathChart_3 } from "../sections/visualizations/BotCareerPathChart_3";
import { BotCareerPathChart_4 } from "../sections/visualizations/BotCareerPathChart_4";
import { AuditTrailChart } from "../sections/visualizations/AuditTrailChart";
import { AuditTrailChart_2 } from "../sections/visualizations/AuditTrailChart_2";
import { AuditTrailChart_3 } from "../sections/visualizations/AuditTrailChart_3";
import { AuditTrailChart_4 } from "../sections/visualizations/AuditTrailChart_4";
import { KernelVersionChart } from "../sections/visualizations/KernelVersionChart";
import { KernelVersionChart_2 } from "../sections/visualizations/KernelVersionChart_2";
import { KernelVersionChart_3 } from "../sections/visualizations/KernelVersionChart_3";
import { KernelVersionChart_4 } from "../sections/visualizations/KernelVersionChart_4";
import { ProcessGraphChart } from "../sections/visualizations/ProcessGraphChart";
import { ProcessGraphChart_2 } from "../sections/visualizations/ProcessGraphChart_2";
import { ProcessGraphChart_3 } from "../sections/visualizations/ProcessGraphChart_3";
import { ProcessGraphChart_4 } from "../sections/visualizations/ProcessGraphChart_4";
import { UrlToCompanyChart } from "../sections/visualizations/UrlToCompanyChart";
import { UrlToCompanyChart_2 } from "../sections/visualizations/UrlToCompanyChart_2";
import { UrlToCompanyChart_3 } from "../sections/visualizations/UrlToCompanyChart_3";
import { UrlToCompanyChart_4 } from "../sections/visualizations/UrlToCompanyChart_4";
import { AutonomyLadderChart } from "../sections/visualizations/AutonomyLadderChart";
import { AutonomyLadderChart_2 } from "../sections/visualizations/AutonomyLadderChart_2";
import { AutonomyLadderChart_3 } from "../sections/visualizations/AutonomyLadderChart_3";
import { AutonomyLadderChart_4 } from "../sections/visualizations/AutonomyLadderChart_4";

const COVER_SLUGS = [
  "humans-decide-bots-prepare",
  "from-url-to-department-in-one-paste",
  "a-process-is-a-graph-with-approval-gates",
  "a-kernel-is-a-contract",
  "every-action-leaves-a-receipt",
  "ai-company-operating-system",
  "ai-workforce-roi",
  "a-bot-with-a-career-path",
];

const CHARTS: {
  name: string;
  /** One-line summary of the _4 (content alternative) variant's finding. */
  caption4: string;
  variants: [ComponentType, ComponentType, ComponentType, ComponentType];
}[] = [
  {
    name: "RaisyStackChart",
    caption4: "4 = layer fan-out (block count per stack layer: 1 → 2 → 3 → 9)",
    variants: [RaisyStackChart, RaisyStackChart_2, RaisyStackChart_3, RaisyStackChart_4],
  },
  {
    name: "CostGapChart",
    caption4: "4 = per-function cost ratios (~29×–71× cheaper, log-scaled)",
    variants: [CostGapChart, CostGapChart_2, CostGapChart_3, CostGapChart_4],
  },
  {
    name: "BotCareerPathChart",
    caption4: "4 = the handover point (autonomy overtakes human review)",
    variants: [BotCareerPathChart, BotCareerPathChart_2, BotCareerPathChart_3, BotCareerPathChart_4],
  },
  {
    name: "AuditTrailChart",
    caption4: "4 = AI Act logging timeline (the law's dates)",
    variants: [AuditTrailChart, AuditTrailChart_2, AuditTrailChart_3, AuditTrailChart_4],
  },
  {
    name: "KernelVersionChart",
    caption4: "4 = GPT-3.5 vs GPT-4 drift direction (unpredictable)",
    variants: [KernelVersionChart, KernelVersionChart_2, KernelVersionChart_3, KernelVersionChart_4],
  },
  {
    name: "ProcessGraphChart",
    caption4: "4 = execution stops at the gate (sequence view with pause band)",
    variants: [ProcessGraphChart, ProcessGraphChart_2, ProcessGraphChart_3, ProcessGraphChart_4],
  },
  {
    name: "UrlToCompanyChart",
    caption4: "4 = company accumulates (cumulative build-up of artifacts)",
    variants: [UrlToCompanyChart, UrlToCompanyChart_2, UrlToCompanyChart_3, UrlToCompanyChart_4],
  },
  {
    name: "AutonomyLadderChart",
    caption4: "4 = oversight never disappears (residual human-review share per mode)",
    variants: [AutonomyLadderChart, AutonomyLadderChart_2, AutonomyLadderChart_3, AutonomyLadderChart_4],
  },
];

const FIGURES = [
  "ai-company-operating-system-flow.png",
  "a-bot-autonomy-earned.png",
];

const VARIANT_LABELS = ["1 current", "2 light", "3 dark"];
const CHART_VARIANT_LABELS = [...VARIANT_LABELS, "4 content"];

const variantSrc = (base: string, index: number) => {
  if (index === 0) return `/blog/${base}`;
  const dot = base.lastIndexOf(".");
  return `/blog/${base.slice(0, dot)}_${index + 1}${base.slice(dot)}`;
};

const ImageRow = ({ name, base }: { name: string; base: string }) => (
  <div className="mb-10">
    <h3 className="mb-3 text-lg font-semibold text-neutral-200">{name}</h3>
    <div className="grid grid-cols-3 gap-4">
      {VARIANT_LABELS.map((label, i) => (
        <figure key={label} className="min-w-0">
          <img
            src={variantSrc(base, i)}
            alt={`${name}, ${label}`}
            className="w-full rounded-lg border border-neutral-700 bg-neutral-800"
          />
          <figcaption className="mt-2 text-sm font-medium text-neutral-400">{label}</figcaption>
        </figure>
      ))}
    </div>
  </div>
);

export const BlogComparePage = () => (
  <div className="min-h-screen bg-neutral-900 px-6 pb-24 text-neutral-100">
    <nav className="sticky top-0 z-10 -mx-6 mb-8 border-b border-neutral-700 bg-neutral-900/95 px-6 py-4 backdrop-blur">
      <div className="flex flex-wrap items-center gap-6">
        <h1 className="text-xl font-bold">Blog visual comparison, pick one per row</h1>
        <div className="flex gap-4 text-sm">
          <a href="#covers" className="text-sky-400 hover:underline">Covers</a>
          <a href="#charts" className="text-sky-400 hover:underline">Charts</a>
          <a href="#figures" className="text-sky-400 hover:underline">In-body figures</a>
        </div>
      </div>
    </nav>

    <section id="covers" className="mb-16 scroll-mt-24">
      <h2 className="mb-6 border-b border-neutral-700 pb-2 text-2xl font-bold">Covers</h2>
      {COVER_SLUGS.map((slug) => (
        <ImageRow key={slug} name={slug} base={`${slug}.jpg`} />
      ))}
    </section>

    <section id="charts" className="mb-16 scroll-mt-24">
      <h2 className="mb-6 border-b border-neutral-700 pb-2 text-2xl font-bold">Charts</h2>
      {CHARTS.map(({ name, caption4, variants }) => (
        <div key={name} className="mb-10">
          <h3 className="text-lg font-semibold text-neutral-200">{name}</h3>
          <p className="mb-3 text-sm text-neutral-400">{caption4}</p>
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            {variants.map((Chart, i) => (
              <figure key={CHART_VARIANT_LABELS[i]} className="min-w-0">
                <div className="min-w-0 overflow-hidden rounded-lg border border-neutral-700">
                  <Chart />
                </div>
                <figcaption className="mt-2 text-sm font-medium text-neutral-400">
                  {CHART_VARIANT_LABELS[i]}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      ))}
    </section>

    <section id="figures" className="scroll-mt-24">
      <h2 className="mb-6 border-b border-neutral-700 pb-2 text-2xl font-bold">In-body figures</h2>
      {FIGURES.map((fig) => (
        <ImageRow key={fig} name={fig} base={fig} />
      ))}
    </section>
  </div>
);
